/*
 * File: DeploymentCard.tsx
 * Project: rosita
 * -----
 * Last Modified: 26 Feb 2022 10:52:07 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { CheckOutlined } from '@ant-design/icons';
import { Button, Card, Col, Divider, Row, Tooltip, Typography } from 'antd';
import { InstallLocation } from 'components/Search/DeploymentCard/components';
import { ShippingLocation } from 'components/Search/DeploymentCard/components/ShippingLocation';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SparingRequest } from 'models/SparingRequests';
import moment from 'moment';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useGetSparingRequestConfigurationTypesQuery } from 'redux/services/calvin/sparingRequestConfigTypes';
import { addSelectedSparingRequests, removeSelectedSparingRequests } from 'redux/slices/selectedDeploymentRequestSlice';
import { ReduxState, useAppDispatch } from 'redux/store';

const { Text, Title } = Typography;

const styles: InlineStylesModel = {
  blackFont: {
    color: '#555555'
  },
  bottomRightItem: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  deploymentStatus: {
    textAlign: 'right'
  },
  image: {
    height: '40px',
    opacity: 0.4
  },
  marginBottom: {},
  column: {
    display: 'flex',
    flexDirection: 'column'
  },
  text: {
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'top'
  },
  card: {
    borderRadius: 5
  },
  cardBody: {
    padding: 10,
    paddingRight: 15,
    paddingLeft: 15
  },
  largeText: {
    fontSize: '18px',
    fontFamily: 'Poppins',
    fontWeight: 700,
    color: '#000'
  },
  title: {
    fontSize: '15px',
    fontWeight: 500,
    color: '#636770'
  },
  subTitle: {
    fontSize: '15px',
    color: '#A0A3AE',
    minHeight: '27px'
  },
  smallText: {
    fontSize: '11px',
    color: 'lightGrey'
  },
  status: {
    fontSize: '16px',
    fontWeight: 700
  },
  draft: {
    color: 'orange'
  },
  posted: {
    color: '#4E937A'
  },
  soNumber: {
    fontSize: '14px',
    color: '#5868DD'
  },
  poNumber: {
    fontSize: '14px',
    color: 'gray'
  },
  customerId: {
    fontSize: '16px',
    color: '#636770'
  },
  tooltipStyle: {
    fontSize: 12,
    textAlign: 'center'
  }
};

interface Props {
  sparingRequest: SparingRequest;
}

export const RecoverDeploymentCard: React.FC<Props> = ({ sparingRequest }) => {
  const { acuityContext } = useSelector((state: ReduxState) => state.app);
  const { data: requestTypeData } = useGetSparingRequestConfigurationTypesQuery();
  const dispatch = useAppDispatch();
  const { selectedSparingRequests } = useSelector((state: ReduxState) => state.selectedSparingRequests);

  const { createdDateTime, requestedDeliveryDate, requestNumber, createdByFullName, status, sparingRequestTypeId } = sparingRequest;
  const [selectedRequestRuleTypeName, setSelectedRequestRuleTypeName] = React.useState(requestTypeData?.data.find((type) => sparingRequestTypeId === type.id)?.name ?? '');
  const formattedCreatedDate = moment(createdDateTime).format('MM/DD/YYYY');
  const formattedRequestedDeliveryDate = requestedDeliveryDate !== null ? moment(requestedDeliveryDate).format('MM/DD/YYYY') : 'TBD';
  // const mdsiOrderNumber = salesOrderNumber === null ? 'Pending' : salesOrderNumber;
  // const projectName = project && project.name ? project.name : 'None';

  // useEffect(() => {
  //   const requestTypeName = requestTypeData?.data.find((type) => deploymentRequestTypeId === type.id)?.name || null;

  //   if (requestTypeName) setSelectedRequestRuleTypeName(requestTypeName);
  // }, [deploymentRequest]);

  const requestNumberJSX = (
    <Tooltip title={requestNumber}>
      <Typography.Text ellipsis style={styles.largeText}>
        {requestNumber}
      </Typography.Text>
    </Tooltip>
  );

  const isSelected = selectedSparingRequests.some((item: any) => item.id === sparingRequest.id);

  const handleToggleSelectProduct = (): void => {
    if (isSelected) {
      dispatch(removeSelectedSparingRequests(sparingRequest));
    } else {
      dispatch(addSelectedSparingRequests(sparingRequest));
    }
  };

  const linkedCardJSX = (
    <Col onClick={handleToggleSelectProduct}>
      <Row gutter={8} justify="space-between">
        <Col span={18}>{requestNumberJSX}</Col>
        <Col>
          {/* <Typography style={status === 'Draft' ? { ...styles.status, ...styles.draft } : status === 'Submitted' || status === 'Posted' ? { ...styles.status, ...styles.posted } : styles.status}>
            <Tooltip title="Status" placement="left" autoAdjustOverflow={false} overlayInnerStyle={styles.tooltipStyle}>
              {status === 'Submitted' ? 'Posted' : status}
            </Tooltip>
          </Typography> */}
          <Button style={{ borderRadius: '50%' }} type={isSelected ? 'primary' : 'default'} icon={isSelected ? <CheckOutlined /> : ' '} />
        </Col>
      </Row>
      <Row gutter={8} justify="space-between">
        <Col>
          <Typography className="narrow-ellipsis" style={styles.customerId}>
            <Tooltip title="Reference Number" placement="right" autoAdjustOverflow={false} overlayInnerStyle={styles.tooltipStyle}>
              {sparingRequest.customerReferenceNumber || 'n/a'}
            </Tooltip>
          </Typography>
        </Col>
      </Row>
      <Row gutter={8} justify="space-between">
        <Col>
          <Typography style={styles.subTitle}>
            <Tooltip title="Deployment Request Type" placement="right" autoAdjustOverflow={false} overlayInnerStyle={styles.tooltipStyle}>
              {selectedRequestRuleTypeName}
            </Tooltip>
          </Typography>
        </Col>
        <Col>
          <Typography style={styles.text}>
            <Tooltip title="Created By" placement="left" autoAdjustOverflow={false} overlayInnerStyle={styles.tooltipStyle}>
              {createdByFullName}
            </Tooltip>
          </Typography>
        </Col>
      </Row>
      <Row justify="space-between">
        <Col style={{ textAlign: 'end' }}>
          <Typography.Paragraph style={{ fontSize: '14px', color: 'gray' }}>
            <Tooltip title="Requested Delivery Date" placement="bottom" autoAdjustOverflow={false} overlayInnerStyle={styles.tooltipStyle}>
              {formattedRequestedDeliveryDate}
            </Tooltip>
          </Typography.Paragraph>
        </Col>
        <Col style={{ textAlign: 'end' }}>
          <Typography.Paragraph style={{ fontSize: '14px', color: 'gray' }}>
            <Tooltip title="Created Date" placement="bottom" autoAdjustOverflow={false} overlayInnerStyle={styles.tooltipStyle}>
              {formattedCreatedDate}
            </Tooltip>
          </Typography.Paragraph>
        </Col>
      </Row>
      {/* <Row>
        <Col style={{ textAlign: 'end', minHeight: 20 }}>
          <Typography.Paragraph style={{ fontSize: '14px', color: 'gray' }}>
            <Tooltip title="Project Name" placement="right" autoAdjustOverflow={false} overlayInnerStyle={styles.tooltipStyle}>
              {projectName && projectName !== 'None' ? projectName : null}
            </Tooltip>
          </Typography.Paragraph>
        </Col>
      </Row> */}
      <Divider />
      <Row style={{ minHeight: 80 }}>
        <Col style={{ textAlign: 'start', marginLeft: 10 }} span={10}>
          <Typography.Paragraph style={{ fontSize: '14px', color: 'gray' }}>
            <Tooltip title="Install Location" placement="top" autoAdjustOverflow={false} overlayInnerStyle={styles.tooltipStyle}>
              <InstallLocation deployment={sparingRequest} />
            </Tooltip>
          </Typography.Paragraph>
        </Col>
        <Col span={2}>
          <Divider type="vertical" style={{ fontSize: 80 }} />
        </Col>
        <Col style={{ textAlign: 'start', marginLeft: 10 }} span={10}>
          <Typography.Paragraph style={{ fontSize: '14px', color: 'gray' }}>
            <Tooltip title="Install Location" placement="top" autoAdjustOverflow={false} overlayInnerStyle={styles.tooltipStyle}>
              <ShippingLocation deployment={sparingRequest} />
            </Tooltip>
          </Typography.Paragraph>
        </Col>
      </Row>
    </Col>
  );

  return (
    <Card style={styles.card} bodyStyle={styles.cardBody} hoverable>
      {linkedCardJSX}
    </Card>
  );
};
